import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { store } from 'redux/store';
import { selectDVCPMATrackingForecastTriggerRequest, setChartData, setTriggerRequest } from 'redux/reducers/meteorologyAgroTrackingForecastDetailedViewControlPanelReducer';
import { useLazyGetAgroTrackingForecastChartDataQuery } from 'services/api/Meteorology/Agro/TrackingForecast/DetailedView';
import type { ResponseError } from 'services/api/base';
import type { ChartStatus, ChartType } from 'types/Meteorology/Agro/TrackingForecast/DetailedView';
import type { GetChartDataParams } from 'types/Meteorology/Agro/TrackingForecast/DetailedView/getChartData';

import useChartDataParams from './useChartDataParams';

export default function useChartRequestTrigger() {
  const [baseTrigger] = useLazyGetAgroTrackingForecastChartDataQuery();
  const trigger = useSelector(selectDVCPMATrackingForecastTriggerRequest);
  const baseParams = useChartDataParams();

  useEffect(() => {
    if (!baseParams) {
      store.dispatch(setTriggerRequest(undefined));
      return;
    }

    const requestData = async (params: GetChartDataParams) => {
      const { subpage, chartType } = params;
      const status: ChartStatus = { data: undefined, error: undefined, isFetching: true };

      store.dispatch(setChartData([subpage, chartType, { ...status }]));

      const res = await baseTrigger(params);
      status.data = res.data?.data ? res.data.data.option : undefined;
      status.error = res.error as ResponseError;
      status.isFetching = false;

      store.dispatch(setChartData([subpage, chartType, { ...status }]));
    };

    const triggerFunc = async () => {
      const { chartType } = baseParams;
      const secondaryType: ChartType = (chartType === 'line') ? 'heatmap' : 'line';
      const secondaryParams = { ...baseParams, chartType: secondaryType };

      // Em paralelo, não em série: os dois gráficos escrevem em slots distintos de
      // `chartData[subpage][chartType]` e nenhum depende do resultado do outro, então
      // encadear só somava as duas latências. O gráfico visível continua partindo no
      // mesmo instante — quem deixa de esperar é o secundário. Erro de um não afeta o
      // outro: `requestData` captura o erro no próprio slot e não rejeita.
      await Promise.all([
        requestData(baseParams),
        requestData(secondaryParams),
      ]);
    };

    store.dispatch(setTriggerRequest(triggerFunc));
  }, [baseTrigger, baseParams]);

  return trigger;
}
